import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  Button
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  DoneAll as DoneAllIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useJobs, Job } from '../../contexts/JobsContext';
import { formatDistanceToNow } from 'date-fns';
import { es, enGB } from 'date-fns/locale';

export const NotificationCenter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { activeJobs, completedJobs, hasUnread, markAsRead, clearCompletedJobs } = useJobs();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    markAsRead();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const allJobs = [...activeJobs, ...completedJobs];
  const hasJobs = allJobs.length > 0;
  
  const dateLocale = i18n.language === 'es' ? es : enGB;

  const JobItem: React.FC<{ job: Job }> = ({ job }) => {
    let icon;
    let color;
    let secondaryText;

    switch (job.status) {
      case 'PROCESSING':
        icon = <CircularProgress size={20} color="secondary" />;
        color = theme.palette.info.main;
        secondaryText = t('jobs.processing', 'Procesando...');
        break;
      case 'COMPLETED':
        icon = <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
        color = theme.palette.success.main;
        secondaryText = t('jobs.completed', 'Completado') + (job.endTime ? ` • ${formatDistanceToNow(job.endTime, { addSuffix: true, locale: dateLocale })}` : '');
        break;
      case 'ERROR':
        icon = <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
        color = theme.palette.error.main;
        secondaryText = job.error || t('jobs.error', 'Error');
        break;
      default:
        icon = <ScheduleIcon fontSize="small" color="action" />;
        color = theme.palette.text.secondary;
        secondaryText = t('jobs.pending', 'Pendiente');
    }

    return (
      <ListItem alignItems="flex-start" sx={{ px: 2, py: 1 }}>
        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {job.title}
            </Typography>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {secondaryText}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  return (
    <>
      <Tooltip title={t('notifications.title', 'Notificaciones y Tareas')}>
        <IconButton
          onClick={handleClick}
          color="inherit"
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Badge
            color="error"
            variant="dot"
            invisible={!hasUnread && activeJobs.length === 0}
            overlap="circular"
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 500,
            overflow: 'hidden',
            mt: 1.5,
            borderRadius: 2,
            boxShadow: theme.shadows[8]
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: theme.palette.background.default }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('notifications.header', 'Actividad Reciente')}
          </Typography>
          {completedJobs.length > 0 && (
             <Tooltip title={t('notifications.clear', 'Limpiar historial')}>
                <IconButton size="small" onClick={clearCompletedJobs}>
                    <ClearAllIcon fontSize="small" />
                </IconButton>
             </Tooltip>
          )}
        </Box>
        <Divider />
        
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {!hasJobs ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <DoneAllIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {t('notifications.empty', 'No hay actividad reciente')}
                    </Typography>
                </Box>
            ) : (
                <List disablePadding>
                    {activeJobs.map(job => (
                        <React.Fragment key={job.id}>
                            <JobItem job={job} />
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                    {completedJobs.map(job => (
                        <React.Fragment key={job.id}>
                             <JobItem job={job} />
                             <Divider component="li" />
                        </React.Fragment>
                    ))}
                </List>
            )}
        </Box>
        
        {activeJobs.length > 0 && (
             <Box sx={{ p: 1, bgcolor: theme.palette.action.hover, textAlign: 'center' }}>
                 <Typography variant="caption" color="primary" fontWeight="bold">
                     {activeJobs.length} {activeJobs.length === 1 ? t('jobs.process_running', 'proceso activo') : t('jobs.processes_running', 'procesos activos')}
                 </Typography>
             </Box>
        )}
      </Menu>
    </>
  );
};
